/**
 * NIGHTBOUND - mood derivation.
 *
 * Mood is *derived*, never stored. It comes from the creature's needs and
 * current activity, which keeps the database honest and the behaviour
 * consistent between client and server.
 */

import { NEED_THRESHOLDS } from './constants'
import type { MoodId } from './species'

export interface MoodInput {
  hunger: number
  energy: number
  hygiene: number
  bond: number
  sanity: number
  currentHp: number
  maxHp: number
  sleeping: boolean
}

export interface MoodDefinition {
  id: MoodId
  /** Player-facing label. */
  label: string
  /** Short atmosphere line shown under the creature. */
  hint: string
  /** Tailwind-ish accent token name used by the UI. */
  tone: 'warm' | 'cold' | 'rot' | 'blood' | 'bone' | 'ash'
  /** Creature renderer state this mood maps to by default. */
  visualState:
    | 'IDLE'
    | 'HAPPY'
    | 'HUNGRY'
    | 'DIRTY'
    | 'SLEEPING'
    | 'INJURED'
}

export const MOODS: Record<MoodId, MoodDefinition> = {
  HAPPY: {
    id: 'HAPPY',
    label: 'Content',
    hint: 'Warm, settled, close to you.',
    tone: 'warm',
    visualState: 'HAPPY',
  },
  CONTENT: {
    id: 'CONTENT',
    label: 'Settled',
    hint: 'Quiet and watchful.',
    tone: 'bone',
    visualState: 'IDLE',
  },
  HUNGRY: {
    id: 'HUNGRY',
    label: 'Hungry',
    hint: 'Restless. It keeps looking toward the feeding corner.',
    tone: 'blood',
    visualState: 'HUNGRY',
  },
  DIRTY: {
    id: 'DIRTY',
    label: 'Filthy',
    hint: 'It will not settle until it is clean.',
    tone: 'rot',
    visualState: 'DIRTY',
  },
  TIRED: {
    id: 'TIRED',
    label: 'Exhausted',
    hint: 'Too tired to do anything well.',
    tone: 'ash',
    visualState: 'IDLE',
  },
  LONELY: {
    id: 'LONELY',
    label: 'Lonely',
    hint: 'It has stopped expecting you.',
    tone: 'cold',
    visualState: 'IDLE',
  },
  UNWELL: {
    id: 'UNWELL',
    label: 'Unwell',
    hint: 'Something is wrong with it.',
    tone: 'blood',
    visualState: 'INJURED',
  },
  SLEEPING: {
    id: 'SLEEPING',
    label: 'Sleeping',
    hint: 'Breathing slowly in the dark.',
    tone: 'cold',
    visualState: 'SLEEPING',
  },
}

/**
 * Priority order matters: a sleeping creature is SLEEPING even if hungry, but
 * an unwell creature never pretends to be settled.
 */
export function deriveMood(input: MoodInput): MoodId {
  const hpRatio = input.maxHp > 0 ? input.currentHp / input.maxHp : 1
  const criticalNeeds =
    (input.hunger <= 8 ? 1 : 0) +
    (input.hygiene <= 8 ? 1 : 0) +
    (input.energy <= 5 ? 1 : 0)

  if (input.sleeping) return 'SLEEPING'
  if (criticalNeeds >= 2 || hpRatio <= NEED_THRESHOLDS.unwellHpRatio) return 'UNWELL'
  if (input.hunger < NEED_THRESHOLDS.hunger) return 'HUNGRY'
  if (input.hygiene < NEED_THRESHOLDS.hygiene) return 'DIRTY'
  if (input.energy < NEED_THRESHOLDS.energy) return 'TIRED'
  if (input.bond < NEED_THRESHOLDS.bond) return 'LONELY'

  const wellbeing =
    (input.hunger + input.hygiene + input.energy + input.bond + input.sanity) / 5
  return wellbeing >= 72 ? 'HAPPY' : 'CONTENT'
}

export function moodOf(input: MoodInput): MoodDefinition {
  return MOODS[deriveMood(input)]
}

/** Low sanity colours the creature's behaviour without changing its mood. */
export type SanityBand = 'STEADY' | 'UNSETTLED' | 'DISTURBED' | 'FRACTURED'

export function sanityBand(sanity: number): SanityBand {
  if (sanity < 15) return 'FRACTURED'
  if (sanity < 35) return 'DISTURBED'
  if (sanity < 60) return 'UNSETTLED'
  return 'STEADY'
}
